import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Divider,
  Grid2,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CheckCircleOutline,
  CloudUploadOutlined,
  ExpandLess,
  ExpandMore,
  ImageNotSupportedOutlined,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import CustomTypography from "../common/form/CustomTypography";
import AppButton from "../common/buttons/AppButton";
import AppDropDown from "../common/filter/AppDropDown";
import EyeIcon from "../../../assets/eyeframe.png";
import type { FilterValue } from "../../../models/Filters/FilterValue";
import { useStores } from "../../../use-stores";

interface PodProductPsdMappingsCardProps {
  loadingOptions?: boolean;
  uploading?: boolean;
  onUploadPsd: () => void | Promise<void>;
}

const SECTION_PAPER_SX = {
  p: { xs: 2, sm: 2.5 },
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "none",
  bgcolor: "background.paper",
} as const;

function StepBadge({ step }: { step: number }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 28,
        height: 28,
        borderRadius: 1.25,
        bgcolor: "primary.main",
        color: "primary.contrastText",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.8rem",
        fontWeight: 800,
        flexShrink: 0,
      }}
    >
      {step}
    </Box>
  );
}

const PodProductPsdMappingsCard: React.FC<PodProductPsdMappingsCardProps> = ({
  loadingOptions = false,
  uploading = false,
  onUploadPsd,
}) => {
  const theme = useTheme();
  const { podCatalogStore } = useStores();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(true);

  const placementSelected = Boolean(podCatalogStore.detailPsdPlacementTypeId);
  const fileSelected = Boolean(podCatalogStore.detailPsdFile);
  const canChooseFile = placementSelected && !loadingOptions && !uploading;
  const canUpload =
    placementSelected && fileSelected && !loadingOptions && !uploading;

  const selectedPlacementId = Number(podCatalogStore.detailPsdPlacementTypeId);
  const existingForPlacement = podCatalogStore.detailPsdTemplates.find(
    (psd) => psd.placement_type_id === selectedPlacementId
  );

  const placementTypeOptions: FilterValue[] = useMemo(
    () => [
      { id: "", value: "Select placement (e.g. Front, Back)" },
      ...podCatalogStore.detailPsdPlacementTypes.map((type) => ({
        id: String(type.id),
        value: type.name,
      })),
    ],
    [podCatalogStore.detailPsdPlacementTypes]
  );

  const uploadedCount = podCatalogStore.detailPsdTemplates.length;

  return (
    <Card sx={{ marginBottom: 2 }}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <img src={EyeIcon} alt="" style={{ width: 30, height: 30 }} />
            <CustomTypography variant="heading5">PSD Configuration</CustomTypography>
            {uploadedCount > 0 && (
              <Chip
                label={`${uploadedCount} uploaded`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ borderRadius: 2, height: 22, fontSize: "0.7rem" }}
              />
            )}
          </Box>
        }
        subheader={
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560, fontSize: "12px" }}>
            Mockup templates for this product. Choose where the design sits on the product, then
            upload one PSD per placement.
          </Typography>
        }
        action={
          <IconButton
            aria-label={open ? "Collapse PSD section" : "Expand PSD section"}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((expanded) => !expanded);
            }}
          >
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
        sx={{
          cursor: "pointer",
          px: 3,
          py: 2,
          borderBottom: open
            ? `1px solid ${theme.palette.customColors?.lightBorder ?? theme.palette.divider}`
            : "none",
          "&:hover": { bgcolor: "action.hover" },
        }}
        onClick={() => setOpen((expanded) => !expanded)}
      />
      <Collapse in={open} timeout="auto" unmountOnExit>
        <CardContent sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 3, pb: 3 }}>
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2" component="div">
              <strong>How it works:</strong> (1) Select placement — which view or print area this PSD
              is for. (2) Choose your <code>.psd</code> file. (3) Upload. Repeat for each placement
              (e.g. Front and Back). Re-uploading the same placement replaces the previous file.
            </Typography>
          </Alert>

          <Paper sx={SECTION_PAPER_SX}>
            <Grid2 container spacing={{ xs: 2.5, lg: 3 }} alignItems="stretch">
              <Grid2 size={{ xs: 12, lg: 6 }}>
                <Stack spacing={2} height="100%">
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <StepBadge step={1} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Placement
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Required before you can attach a file
                      </Typography>
                    </Box>
                  </Stack>

                  <AppDropDown
                    title="Placement type"
                    isMultiSelect={false}
                    list={placementTypeOptions}
                    value={podCatalogStore.detailPsdPlacementTypeId}
                    onSingleSelectionChange={(id) => {
                      podCatalogStore.setDetailPsdPlacementTypeId(id || "");
                      if (!id) {
                        podCatalogStore.setDetailPsdFile(null);
                      }
                    }}
                    disabled={loadingOptions || uploading}
                    placeholderText="Select placement (e.g. Front, Back)"
                    width="100%"
                  />
                </Stack>
              </Grid2>

              <Grid2 size={{ xs: 12, lg: 6 }}>
                <Stack spacing={2} height="100%">
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <StepBadge step={2} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        PSD file
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {placementSelected
                          ? "Select a Photoshop (.psd) file for this placement."
                          : "Select a placement first to enable file upload."}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed",
                      borderColor: canChooseFile ? "primary.light" : "divider",
                      borderRadius: 2,
                      bgcolor: canChooseFile ? "action.hover" : "grey.50",
                      opacity: canChooseFile ? 1 : 0.65,
                      px: 2,
                      py: 2.5,
                      textAlign: "center",
                      minHeight: { xs: 140, lg: 168 },
                      transition: "border-color 0.2s, opacity 0.2s",
                    }}
                  >
                    <CloudUploadOutlined
                      sx={{
                        fontSize: 36,
                        color: canChooseFile ? "primary.main" : "text.disabled",
                        mb: 1,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {fileSelected
                        ? podCatalogStore.detailPsdFile?.name
                        : "Adobe Photoshop (.psd) only"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                      Max file size: 5 MB only.
                    </Typography>
                    <AppButton
                      label={fileSelected ? "Change file" : "Choose PSD file"}
                      variant="secondary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={!canChooseFile}
                      customStyles={{ minWidth: 160 }}
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".psd"
                      hidden
                      disabled={!canChooseFile}
                      onChange={(e) => {
                        podCatalogStore.setDetailPsdFile(e.target.files?.[0] || null);
                        e.currentTarget.value = "";
                      }}
                    />
                  </Box>
                </Stack>
              </Grid2>
            </Grid2>

            {existingForPlacement && placementSelected && (
              <Alert severity="warning" sx={{ borderRadius: 1.5, mt: 2.5 }}>
                A PSD is already uploaded for{" "}
                <strong>{existingForPlacement.placement_type_name}</strong>. Uploading again will
                replace it.
              </Alert>
            )}

            <Divider sx={{ my: 2.5 }} />

            <Box display="flex" justifyContent="flex-end" flexWrap="wrap" gap={1.5}>
              <AppButton
                label={uploading ? "Uploading…" : "Upload PSD"}
                variant="primary"
                onClick={onUploadPsd}
                disabled={!canUpload}
                customStyles={{
                  minWidth: 160,
                  background: canUpload
                    ? `linear-gradient(90deg, ${theme.palette.customColors.purpleGradientStart} 0%, ${theme.palette.customColors.purpleGradientEnd} 100%)`
                    : undefined,
                  color: canUpload ? theme.palette.customColors.white : undefined,
                }}
              />
            </Box>
          </Paper>

          <Box sx={{ mt: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              sx={{ mb: 2 }}
            >
              <CustomTypography variant="heading8">Uploaded PSDs</CustomTypography>
              {uploadedCount === 0 && !loadingOptions && (
                <Typography variant="caption" color="text.secondary">
                  None yet — complete the steps above for each placement you need.
                </Typography>
              )}
            </Stack>

            {loadingOptions && uploadedCount === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Loading placements and uploaded files…
              </Typography>
            ) : uploadedCount === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  py: 4,
                  px: 2,
                  textAlign: "center",
                  borderRadius: 2,
                  borderStyle: "dashed",
                }}
              >
                <ImageNotSupportedOutlined sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No PSDs uploaded for this product yet.
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={1.5}>
                {podCatalogStore.detailPsdTemplates.map((psd) => (
                  <Paper
                    key={
                      psd.id != null
                        ? String(psd.id)
                        : `${psd.placement_type_id}-${psd.reference_id ?? "psd"}`
                    }
                    variant="outlined"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
                      <CheckCircleOutline color="success" fontSize="small" />
                      <Box minWidth={0}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {psd.placement_type_name || "Unknown placement"}
                        </Typography>
                        {psd.reference_id ? (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            Ref: {psd.reference_id}
                          </Typography>
                        ) : null}
                      </Box>
                    </Stack>
                    {psd.preview_url ? (
                      <Box
                        component="img"
                        src={psd.preview_url}
                        alt={`${psd.placement_type_name ?? "PSD"} preview`}
                        sx={{
                          width: 64,
                          height: 64,
                          objectFit: "cover",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 1,
                          bgcolor: "grey.100",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <ImageNotSupportedOutlined fontSize="small" color="disabled" />
                      </Box>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default observer(PodProductPsdMappingsCard);
